import React, { Component } from "react";
import { marked } from "marked";
import { MathJax } from "better-react-mathjax";

/**
 * Details Explanation tab panel.
 *
 * Rendered inside the Similar Questions modal, so it deliberately does NOT
 * render its own <Modal> or <MathJaxContext> - it inherits the MathJax context
 * from its parent.
 *
 * The API is called lazily: nothing is requested until the panel is first
 * mounted, which the parent only does once the user opens this tab.
 *
 *   POST {apiBase}/api/solutions/detailed_explaination?question=<question>
 *
 * `question` is sent as a query parameter, matching the swagger definition
 * (question: string | (string | null), in: query).
 */

/* ---------------------------------------------------------------------------
 * Text helpers - local so the Similar Questions modal stays untouched. They
 * mirror its behaviour: MathML -> LaTeX, entity unescaping and whitespace
 * normalisation that preserves markdown block structure.
 * ------------------------------------------------------------------------- */

const safeDecode = (s) => {
  if (!s || typeof s !== "string") return s;
  let out = s;
  try {
    for (let i = 0; i < 3; i++) {
      const dec = decodeURIComponent(out);
      if (dec === out) break;
      out = dec;
    }
  } catch (e) {
    return s;
  }
  return out;
};

const encodeForm = (s) => {
  try {
    if (s === null || typeof s === "undefined") return "";
    const dec = safeDecode(String(s));
    return encodeURIComponent(dec).replace(/%20/g, "+");
  } catch (e) {
    return encodeURIComponent(String(s)).replace(/%20/g, "+");
  }
};

/**
 * Encoder for the `question` parameter. Unlike encodeForm it does NOT decode
 * first: the value is already final LaTeX, and a pre-decode would silently eat
 * any literal percent sequence in it (a "%20" in the question text, "\%" in a
 * formula) and corrupt the LaTeX before it is sent. Spaces become "+" to match
 * the other AI endpoints.
 */
const encodeLatexParam = (s) =>
  encodeURIComponent(s === null || typeof s === "undefined" ? "" : String(s)).replace(
    /%20/g,
    "+"
  );

/**
 * MathType/Wiris write an accent as <mover>/<munder> whose script is the accent
 * glyph itself: u-hat is <mover><mi>u</mi><mo>^</mo></mover>. Emitting that as
 * \overset{^}{u} hands MathJax a "^" with nothing after it, and MathJax then
 * prints "Missing superscript or subscript argument" in place of the whole
 * formula. Accents therefore map to real LaTeX macros instead.
 */
const ACCENT_MACROS = {
  "^": "\\hat",
  "ˆ": "\\hat",
  "̂": "\\hat",
  "~": "\\tilde",
  "˜": "\\tilde",
  "̃": "\\tilde",
  "→": "\\vec",
  "⃗": "\\vec",
  "←": "\\overleftarrow",
  "¯": "\\bar",
  "‾": "\\bar",
  "̄": "\\bar",
  "˙": "\\dot",
  "̇": "\\dot",
  "¨": "\\ddot",
  "̈": "\\ddot",
  "ˇ": "\\check",
  "˘": "\\breve",
  "´": "\\acute",
  "`": "\\grave",
  "_": "\\underline",
};

// Last line of defence: a script operator with no argument after it is the one
// thing that makes MathJax replace a formula with an error box, so any that
// survive conversion are dropped rather than rendered.
const dropDanglingScripts = (s) =>
  String(s).replace(/[\^_](?!\s*(?:\{|\\[a-zA-Z]|[A-Za-z0-9]))/g, "");

const mathMLToLatex = (mathString) => {
  try {
    const parser = new DOMParser();
    let doc = parser.parseFromString(mathString, "application/xml");

    // Malformed MathML yields a <parsererror> document rather than throwing;
    // its text is the browser's own error message, so re-parse leniently.
    if (doc.getElementsByTagName("parsererror").length) {
      doc = parser.parseFromString(mathString, "text/html");
    }

    const kids = (node) =>
      [...node.childNodes].filter(
        (n) => !(n.nodeType === 3 && !(n.nodeValue || "").trim())
      );

    const join = (node) => kids(node).map(convert).join("");
    const at = (c, i) => (c[i] ? convert(c[i]) : "");

    const convert = (node) => {
      if (!node) return "";
      if (node.nodeType === 3) return node.nodeValue || "";
      if (node.nodeType !== 1) return "";
      const name = node.nodeName.toLowerCase().replace(/^m:/, "");

      if (name === "parsererror") return "";

      // MathType stores its own source encoding in <annotation>; it is not
      // meant to be displayed and would otherwise be concatenated onto the
      // real formula as gibberish (MathType@MTEF@5@5@+=feaagKart...).
      if (name === "annotation" || name === "annotation-xml") return "";
      if (name === "mphantom") return "";
      if (name === "mspace") return " ";

      // Purely structural wrappers: unwrap.
      if (
        name === "math" ||
        name === "semantics" ||
        name === "mrow" ||
        name === "mstyle" ||
        name === "mpadded" ||
        name === "menclose" ||
        name === "merror"
      ) {
        return join(node);
      }

      if (name === "msup") {
        const c = kids(node);
        return `${at(c, 0)}^{${at(c, 1)}}`;
      }
      if (name === "msub") {
        const c = kids(node);
        return `${at(c, 0)}_{${at(c, 1)}}`;
      }
      if (name === "msubsup") {
        const c = kids(node);
        return `${at(c, 0)}_{${at(c, 1)}}^{${at(c, 2)}}`;
      }
      if (name === "mfrac") {
        const c = kids(node);
        return `\\frac{${at(c, 0)}}{${at(c, 1)}}`;
      }
      if (name === "msqrt") return `\\sqrt{${join(node)}}`;
      if (name === "mroot") {
        const c = kids(node);
        return `\\sqrt[${at(c, 1)}]{${at(c, 0)}}`;
      }
      if (name === "mover" || name === "munder") {
        const c = kids(node);
        const base = at(c, 0);
        const script = at(c, 1).trim();
        const accent = ACCENT_MACROS[script];
        if (accent) return `${accent}{${base}}`;
        // Not an accent: keep it stacked, but a bare script operator in the
        // overscript would break the expression, so strip those.
        const safe = script.replace(/[\^_]/g, "");
        return name === "mover"
          ? `\\overset{${safe}}{${base}}`
          : `\\underset{${safe}}{${base}}`;
      }
      if (name === "munderover") {
        const c = kids(node);
        return `${at(c, 0)}_{${at(c, 1)}}^{${at(c, 2)}}`;
      }

      if (name === "mfenced") {
        const open = node.getAttribute ? node.getAttribute("open") : null;
        const close = node.getAttribute ? node.getAttribute("close") : null;
        const sepAttr = node.getAttribute ? node.getAttribute("separators") : null;
        const sep = sepAttr === null || typeof sepAttr === "undefined" ? "," : sepAttr.trim();
        const parts = kids(node).map(convert);
        return `${open === null ? "(" : open}${parts.join(sep === "" ? "" : sep)}${
          close === null ? ")" : close
        }`;
      }

      if (name === "mtable") {
        const rows = kids(node).map(convert).filter((r) => r !== "");
        return `\\begin{matrix}${rows.join("\\\\")}\\end{matrix}`;
      }
      if (name === "mtr" || name === "mlabeledtr") return kids(node).map(convert).join("&");
      if (name === "mtd") return join(node);

      if (name === "mi" || name === "mn" || name === "mo" || name === "mtext") {
        // A lone "^" or "_" here is an accent glyph that <mover> did not pick
        // up, never a script operator; passing it on breaks the formula.
        return (node.textContent || "").replace(/[\^_]/g, "");
      }

      // fallback: join children
      return join(node);
    };

    const root = doc.querySelector("math") || doc.documentElement || doc.firstChild;
    const latex = convert(root);

    if (/This page contains the following errors|error on line \d+ at column/i.test(latex)) {
      return "";
    }

    return dropDanglingScripts(latex) || "";
  } catch (e) {
    return "";
  }
};

/**
 * This question bank renders MathType formulas as Wiris <img> elements
 * (class="Wirisformula", see .Wirisformula in App.scss) rather than inline
 * MathML. Stripping tags would delete the formula entirely, so pull the math
 * back out of the image's attributes first.
 */

// Wiris "safeXML" swaps markup characters so MathML can live in an attribute.
const decodeSafeXml = (s) =>
  String(s || "")
    .replace(/«/g, "<")
    .replace(/»/g, ">")
    .replace(/¨/g, '"')
    .replace(/§/g, "&")
    .replace(/`/g, "'");

const imgToMath = (imgHtml) => {
  try {
    const doc = new DOMParser().parseFromString(imgHtml, "text/html");
    const img = doc.querySelector("img");
    if (!img) return null;

    const attr = (...names) => {
      for (const n of names) {
        const v = img.getAttribute(n);
        if (v) return v;
      }
      return null;
    };

    const cls = img.getAttribute("class") || "";
    const dataMathml = attr("data-mathml", "data-mml", "data-mathml-encoded");
    const dataLatex = attr("data-latex", "data-tex", "data-formula");
    const alt = attr("alt", "title", "aria-label", "data-alt") || "";
    const src = img.getAttribute("src") || "";

    const looksLikeMath =
      /wiris|mathtype|formula|math|equation|latex/i.test(cls) ||
      !!dataMathml ||
      !!dataLatex ||
      /«math|<math/i.test(alt);

    // Plain figures/diagrams carry no formula - leave them dropped.
    if (!looksLikeMath) return null;

    // 1) Wiris/MathType: data-mathml holds safeXML-encoded MathML.
    if (dataMathml) {
      const latex = mathMLToLatex(decodeSafeXml(dataMathml));
      if (latex) return `$${latex}$`;
    }

    // 2) Some editors expose the LaTeX directly.
    if (dataLatex) return `$${dataLatex}$`;

    // 3) alt may hold MathML (encoded or raw), else a readable fallback.
    if (alt) {
      const altDecoded = decodeSafeXml(alt);
      if (/<math[\s>]/i.test(altDecoded)) {
        const latex = mathMLToLatex(altDecoded);
        if (latex) return `$${latex}$`;
      }
      return altDecoded;
    }

    // 4) Last resort: the renderer put the formula in the src query string.
    const m = src.match(/[?&](?:latex|formula|mml)=([^&]+)/i);
    if (m) {
      const val = decodeSafeXml(decodeURIComponent(m[1].replace(/\+/g, " ")));
      if (/<math[\s>]/i.test(val)) {
        const latex = mathMLToLatex(val);
        if (latex) return `$${latex}$`;
      }
      return `$${val}$`;
    }

    return null;
  } catch (e) {
    return null;
  }
};

const normalizeMathText = (input) => {
  if (!input) return "";
  let text = String(input);

  // Recover MathType/Wiris formulas from <img> before any tag stripping,
  // otherwise every formula in the question is silently deleted.
  text = text.replace(/<img\b[^>]*>/gi, (m) => {
    const recovered = imgToMath(m);
    return recovered ? ` ${recovered} ` : " ";
  });

  // Namespace prefixes are allowed (<m:math>, <mml:math>) - matching only
  // "<math" would leave those untouched and the tag stripper would delete the
  // whole formula.
  const mathmlMatches = [];
  text = text.replace(/<([a-z][\w-]*:)?math\b[\s\S]*?<\/([a-z][\w-]*:)?math>/gi, (m) => {
    mathmlMatches.push(m);
    return `@@MATHML_REPLACE_${mathmlMatches.length - 1}@@`;
  });

  if (mathmlMatches.length) {
    mathmlMatches.forEach((m, idx) => {
      const latex = mathMLToLatex(m);
      const replacement = latex
        ? `$${latex}$`
        : m.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
      text = text.replace(`@@MATHML_REPLACE_${idx}@@`, () => replacement);
    });
  }

  text = text
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  text = text.replace(/\\\(/g, "$");
  text = text.replace(/\\\)/g, "$");
  // A literal "$$" in a replacement string means one "$" to String.replace, so
  // display delimiters have to be written as a function to survive.
  text = text.replace(/\\\[/g, () => "$$");
  text = text.replace(/\\\]/g, () => "$$");

  // The tag stripper cannot tell "<" in a formula ("a < b") from the start of a
  // tag and would delete everything up to the next ">", taking half the
  // sentence and the tail of a formula with it. Formulas are parked first.
  const formulas = [];
  text = text.replace(/\$\$[\s\S]+?\$\$|\$[^$\n]+?\$/g, (m) => {
    formulas.push(m);
    return `@@MJXKEEP_${formulas.length - 1}@@`;
  });

  text = text.replace(/<[^>]+>/g, " ");

  if (formulas.length) {
    formulas.forEach((m, idx) => {
      text = text.replace(`@@MJXKEEP_${idx}@@`, () => m);
    });
  }

  text = text.replace(/\r\n/g, "\n");
  text = text.replace(/\n{3,}/g, "\n\n");
  text = text
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n");
  text = text.replace(/\n\s*\n+/g, "\n\n").trim();

  return text;
};

/* ---------------------------------------------------------------------------
 * Formula detection for the API response.
 *
 * The model returns a mix of prose and maths, and the maths is not reliably
 * delimited - sometimes $...$, sometimes bare LaTeX (\frac{1}{2}), sometimes
 * a plain equation (3x + 4y + 5 = 0). Two things have to happen:
 *
 *   1. every formula gets wrapped in LaTeX delimiters so MathJax typesets it;
 *   2. formulas are pulled out BEFORE marked runs, because markdown otherwise
 *      eats "_" as emphasis and mangles backslashes inside LaTeX.
 *
 * Everything that is not matched stays plain text and keeps its markdown.
 * ------------------------------------------------------------------------- */

const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// A LaTeX macro with its brace arguments and any scripts:
//   \frac{1}{2}   \sqrt{x+1}   \alpha   \Rightarrow   \sum_{i=1}^{n}
const BARE_LATEX =
  /\\[a-zA-Z]+(?:\s*\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\})*(?:\s*[_^]\s*(?:\{[^{}]*\}|[A-Za-z0-9]))*/g;

// An operand is a single symbol or a number, optionally scripted. Keeping
// operands to one letter is what stops ordinary prose from matching.
const OPERAND =
  "(?:\\d+(?:\\.\\d+)?|\\d*[A-Za-z]\\d*)(?:\\s*[_^]\\s*(?:\\{[^{}]*\\}|[A-Za-z0-9]+))?";

// Undelimited equations: "3x + 4y + 5 = 0", "12 - k", "k/2".
// At least one operator is required, so a bare word can never match. The
// surrounding boundaries stop hyphenated prose ("well-known", "y-coordinate")
// from being mistaken for subtraction. A leading boundary character is matched
// rather than a lookbehind, which older Safari does not support.
const BOUNDARY_BEFORE = "(^|[^A-Za-z0-9_\\\\])";
const BOUNDARY_AFTER = "(?![A-Za-z0-9_])";

const PLAIN_EQ = new RegExp(
  `${BOUNDARY_BEFORE}(${OPERAND}(?:\\s*[-+*/=<>]\\s*${OPERAND})+)${BOUNDARY_AFTER}`,
  "g"
);

// A lone scripted symbol that the equation rule misses: x^2, v_0, a^{n+1}.
const SCRIPTED = new RegExp(
  `${BOUNDARY_BEFORE}(\\d*[A-Za-z]\\d*\\s*[_^]\\s*(?:\\{[^{}]*\\}|[A-Za-z0-9]+))${BOUNDARY_AFTER}`,
  "g"
);

// An extracted macro often has a loose operand beside it ("\Delta = 0"), which
// belongs inside the same formula rather than being left behind as prose.
const ABSORB_RIGHT = new RegExp(
  `@@MJX(\\d+)@@(\\s*[-+*/=<>]\\s*)(${OPERAND})${BOUNDARY_AFTER}`,
  "g"
);
const ABSORB_LEFT = new RegExp(
  `${BOUNDARY_BEFORE}(${OPERAND})(\\s*[-+*/=<>]\\s*)@@MJX(\\d+)@@`,
  "g"
);

const absorbOperands = (input, store) => {
  let text = input;
  let prev = null;
  while (prev !== text) {
    prev = text;
    text = text.replace(ABSORB_RIGHT, (full, i, op, operand) => {
      const a = store[Number(i)];
      if (!a || a.display) return full;
      store.push({ latex: `${a.latex}${op}${operand}`, display: false });
      return `@@MJX${store.length - 1}@@`;
    });
    text = text.replace(ABSORB_LEFT, (full, pre, operand, op, i) => {
      const a = store[Number(i)];
      if (!a || a.display) return full;
      store.push({ latex: `${operand}${op}${a.latex}`, display: false });
      return `${pre}@@MJX${store.length - 1}@@`;
    });
  }
  return text;
};

// Inline formulas separated only by an operator belong in one expression, so
// "\frac{a}{b} + \frac{c}{d}" typesets as a single formula rather than three.
const mergeAdjacent = (input, store) => {
  const re = /@@MJX(\d+)@@(\s*[-+*/=<>]?\s*)@@MJX(\d+)@@/g;
  let text = input;
  let prev = null;
  while (prev !== text) {
    prev = text;
    text = text.replace(re, (full, i, mid, j) => {
      const a = store[Number(i)];
      const b = store[Number(j)];
      if (!a || !b || a.display || b.display) return full;
      store.push({ latex: `${a.latex}${mid}${b.latex}`, display: false });
      return `@@MJX${store.length - 1}@@`;
    });
  }
  return text;
};

/**
 * Splits `src` into formulas and prose, runs markdown over the prose only,
 * then puts the formulas back as MathJax-ready LaTeX. Returns an HTML string.
 */
const renderExplanationHtml = (src) => {
  const input = String(src || "");
  if (!input.trim()) return "";

  const store = [];
  const put = (latex, display) => {
    const clean = String(latex).replace(/\$/g, "").trim();
    if (!clean) return " ";
    store.push({ latex: clean, display });
    return `@@MJX${store.length - 1}@@`;
  };

  let out = input;

  // Explicit delimiters first - highest confidence, so they win any overlap.
  out = out.replace(/```(?:math|latex|tex)\s*\n?([\s\S]+?)```/gi, (m, body) => put(body, true));
  out = out.replace(/\\begin\{([a-zA-Z*]+)\}[\s\S]*?\\end\{\1\}/g, (m) => put(m, true));
  out = out.replace(/\$\$([\s\S]+?)\$\$/g, (m, body) => put(body, true));
  out = out.replace(/\\\[([\s\S]+?)\\\]/g, (m, body) => put(body, true));
  out = out.replace(/\$([^$\n]+?)\$/g, (m, body) => put(body, false));
  out = out.replace(/\\\(([\s\S]+?)\\\)/g, (m, body) => put(body, false));

  // Then the maths the model wrote without any delimiters at all. The boundary
  // character each rule consumes has to be put back.
  out = out.replace(BARE_LATEX, (m) => put(m, false));
  out = out.replace(PLAIN_EQ, (m, pre, body) => `${pre}${put(body, false)}`);
  out = out.replace(SCRIPTED, (m, pre, body) => `${pre}${put(body, false)}`);

  out = absorbOperands(out, store);
  out = mergeAdjacent(out, store);

  // Markdown now only ever sees prose.
  let html = marked.parse(out);

  html = html.replace(/@@MJX(\d+)@@/g, (full, i) => {
    const item = store[Number(i)];
    if (!item) return full;
    const body = escapeHtml(item.latex);
    // A <span> (not <div>) so it stays valid inside a markdown <p>.
    return item.display
      ? `<span class="dx-math-block">$$${body}$$</span>`
      : `<span class="dx-math-inline">$${body}$</span>`;
  });

  return html;
};

/* ---------------------------------------------------------------------------
 * Selected question rendering.
 *
 * The card at the top always shows the question the student selected (the
 * `question` prop) - never a question echoed back by the API. Unlike the copy
 * that is sent to the API, this keeps the original markup (<b>, <sup>, lists,
 * tables, diagrams) so ordinary text renders as ordinary HTML, and hands only
 * the mathematics to MathJax as $...$ / $$...$$.
 * ------------------------------------------------------------------------- */

const decodeEntities = (s) =>
  String(s)
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&");

// The question markup is injected with dangerouslySetInnerHTML, so anything
// executable is removed first. Formatting tags are deliberately kept.
const sanitizeQuestionHtml = (html) =>
  String(html)
    .replace(/<\s*(script|style|iframe|object|embed)\b[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
    .replace(/<\s*\/?\s*(script|style|iframe|object|embed|link|meta|base|form)\b[^>]*>/gi, "")
    .replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, "")
    .replace(/\son[a-z]+\s*=\s*'[^']*'/gi, "")
    .replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, "")
    .replace(/(href|src|xlink:href)\s*=\s*"\s*javascript:[^"]*"/gi, '$1="#"')
    .replace(/(href|src|xlink:href)\s*=\s*'\s*javascript:[^']*'/gi, "$1='#'");

const mathSpan = (latex, display) => {
  const body = escapeHtml(decodeEntities(latex));
  return display
    ? `<span class="dx-math-block">$$${body}$$</span>`
    : `<span class="dx-math-inline">$${body}$</span>`;
};

/**
 * Pulls the maths out of one run of text (never out of a tag or an attribute):
 * explicit delimiters first, then the formulas the question bank stores
 * without any delimiter at all.
 */
const extractQuestionMath = (segment, put) => {
  let out = segment;
  if (!out) return out;

  out = out.replace(/\$\$([\s\S]+?)\$\$/g, (m, body) => put(body, true));
  out = out.replace(/\\\[([\s\S]+?)\\\]/g, (m, body) => put(body, true));
  out = out.replace(/\$([^$\n]+?)\$/g, (m, body) => put(body, false));
  out = out.replace(/\\\(([\s\S]+?)\\\)/g, (m, body) => put(body, false));

  out = out.replace(BARE_LATEX, (m) => put(m, false));
  out = out.replace(PLAIN_EQ, (m, pre, body) => `${pre}${put(body, false)}`);
  out = out.replace(SCRIPTED, (m, pre, body) => `${pre}${put(body, false)}`);

  return out;
};

/**
 * Turns the raw `question` prop into HTML that MathJax can typeset. Returns an
 * HTML string; the caller renders it inside <MathJax>.
 */
const buildQuestionHtml = (rawQuestion) => {
  const raw = String(rawQuestion || "");
  if (!raw.trim()) return "";

  // safeDecode must never run over markup - see buildQuestionForApi.
  const looksLikeHtml = /<[a-z!/][^>]*>/i.test(raw);
  const source = looksLikeHtml ? raw : safeDecode(raw);

  const store = [];
  const put = (latex, display) => {
    const clean = String(latex).replace(/\$/g, "").trim();
    if (!clean) return " ";
    store.push({ latex: clean, display });
    return `@@MJX${store.length - 1}@@`;
  };

  let html;

  if (looksLikeHtml) {
    html = sanitizeQuestionHtml(source);

    // MathType/Wiris formulas travel as <img class="Wirisformula">. A genuine
    // picture (diagram, graph) carries no formula and is left untouched so it
    // still renders as an image.
    html = html.replace(/<img\b[^>]*>/gi, (m) => {
      const recovered = imgToMath(m);
      if (!recovered) return m;
      return /^\$[\s\S]*\$$/.test(recovered)
        ? ` ${put(recovered, false)} `
        : ` ${escapeHtml(recovered)} `;
    });

    // Inline MathML, with or without a namespace prefix.
    html = html.replace(/<([a-z][\w-]*:)?math\b[\s\S]*?<\/([a-z][\w-]*:)?math>/gi, (m) => {
      const latex = mathMLToLatex(m);
      return latex
        ? ` ${put(latex, false)} `
        : ` ${escapeHtml(m.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim())} `;
    });
  } else {
    // Plain text: escape it so it can go through the same pipeline, and keep
    // the author's line breaks (.dx-question-plain sets white-space: pre-wrap).
    html = escapeHtml(decodeEntities(source));
  }

  // Only the text between tags is scanned - tag names and attribute values
  // must survive byte for byte.
  html = html
    .split(/(<[^>]+>)/)
    .map((seg) => (seg.charAt(0) === "<" ? seg : extractQuestionMath(seg, put)))
    .join("");

  // "\Delta = 0" and "\frac{a}{b} + \frac{c}{d}" are single formulas, not two
  // or three sitting next to each other. Tags between two tokens block a merge.
  html = absorbOperands(html, store);
  html = mergeAdjacent(html, store);

  html = html.replace(/@@MJX(\d+)@@/g, (full, i) => {
    const item = store[Number(i)];
    return item ? mathSpan(item.latex, item.display) : full;
  });

  return looksLikeHtml ? html : `<div class="dx-question-plain">${html}</div>`;
};

/**
 * The model often restates the question before answering it. The card above
 * already shows the question the student selected, so a leading "Question:"
 * label and the block it introduces are dropped - never the whole answer.
 */
const stripQuestionEcho = (text) => {
  const src = String(text || "");
  // A label only: "Question:", "**Question**", "### Question". \b keeps
  // "Questions of this type..." out of it.
  const label = src.match(
    /^\s*(?:#{1,6}\s*)?(\*\*|__)?\s*(?:the\s+)?question\b[ \t]*([:.–—-])?[ \t]*(\*\*|__)?[ \t]*([:.–—-])?[ \t]*/i
  );
  if (!label) return src;

  const rest = src.slice(label[0].length);

  // Without any marker or punctuation it is a sentence that happens to start
  // with the word ("Question is ambiguous, but..."), not a label.
  const isLabel = !!(label[1] || label[2] || label[3] || label[4]) || /^\r?\n/.test(rest);
  if (!isLabel) return src;

  const blank = rest.search(/\n\s*\n/);
  const remainder = blank === -1 ? "" : rest.slice(blank).trim();

  // Only an echo followed by real content is removed; if the label introduces
  // everything there is, the text is left exactly as the API sent it.
  return remainder || src;
};

class DetailsExplanation extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: null,
      explanation: "",
    };
    this.isMounted_ = false;
    // Memo for the selected question: building it parses MathML, so it is only
    // redone when the prop itself changes.
    this.questionHtmlCache = null;
  }

  componentDidMount() {
    this.isMounted_ = true;
    this.load();
  }

  componentDidUpdate(prevProps) {
    // A different question (new modal session) needs a fresh explanation.
    if (prevProps.question !== this.props.question) this.load();
  }

  componentWillUnmount() {
    this.isMounted_ = false;
  }

  getApiBase = () => this.props.apiBase || "https://ai.rizee.in";

  getCacheKey = () => {
    const questionId = this.props.question_id || "no_id";
    return `ai_details_explanation_${questionId}`;
  };

  loadFromCache = () => {
    try {
      const cached = localStorage.getItem(this.getCacheKey());
      if (!cached) return null;
      return JSON.parse(cached);
    } catch (e) {
      return null;
    }
  };

  saveToCache = (data) => {
    try {
      localStorage.setItem(this.getCacheKey(), JSON.stringify(data));
    } catch (e) {
      // ignore quota / privacy-mode failures
    }
  };

  // The question is sent as LaTeX: prose stays plain text and every formula is
  // emitted as $...$ by normalizeMathText.
  buildQuestionForApi = () => {
    const raw = String(this.props.question || "");

    // safeDecode exists to undo double URL-encoding of a plain string. It must
    // never run over markup: a Wiris <img src> commonly holds percent-encoded
    // MathML, and decoding that mid-attribute breaks the tag so attribute soup
    // leaks into the question. Only decode when the input is not already HTML.
    const looksLikeHtml = /<[a-z!/][^>]*>/i.test(raw);
    const decoded = looksLikeHtml ? raw : safeDecode(raw);

    const normalized = normalizeMathText(decoded);

    // Diagnostic: the question clearly carried maths but none survived, which
    // means the formula is stored in a shape the recovery does not know about.
    // Log the raw markup so the carrier can be identified.
    if (process.env.NODE_ENV !== "production") {
      const hadMathMarkup = /<img\b|<([a-z][\w-]*:)?math\b|data-(?:mathml|mml|latex|tex)/i.test(
        decoded
      );
      if (hadMathMarkup && !/\$/.test(normalized)) {
        // eslint-disable-next-line no-console
        console.warn(
          "[DetailsExplanation] question contains maths markup but no formula was recovered.\n" +
            "Raw question HTML (paste this to identify the carrier):\n",
          decoded
        );
      }
    }

    if (normalized) return normalized;
    return decoded
      .replace(/<\/?[^>]+(>|$)/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  // The question shown to the student is always the one selected in the parent
  // modal, built straight from the prop and independent of the API call.
  getQuestionHtml = () => {
    const raw = String(this.props.question || "");
    if (!this.questionHtmlCache || this.questionHtmlCache.raw !== raw) {
      this.questionHtmlCache = { raw, html: buildQuestionHtml(raw) };
    }
    return this.questionHtmlCache.html;
  };

  load = () => {
    if (this.props.question_id) {
      const cached = this.loadFromCache();
      if (cached && cached.explanation && String(cached.explanation).trim() !== "") {
        this.setState({ loading: false, error: null, explanation: cached.explanation });
        return;
      }
    }

    this.fetchDetailsExplanation(this.buildQuestionForApi());
  };

  fetchDetailsExplanation = async (questionTextArg) => {
    const questionText =
      typeof questionTextArg === "string" ? questionTextArg : this.buildQuestionForApi();

    this.setState({ loading: true, error: null, explanation: "" });

    try {
      // questionText is the full question with every formula as LaTeX ($...$).
      const url = `${this.getApiBase()}/api/solutions/detailed_explaination?question=${encodeLatexParam(
        questionText
      )}`;

      const res = await fetch(url, {
        method: "POST",
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Details Explanation API error ${res.status}: ${t}`);
      }

      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch (e) {
        data = raw;
      }

      // Accept the various shapes the AI endpoints return, including the
      // 'exaplation' typo used by the sibling explain endpoints.
      let explanation = null;
      if (data && typeof data === "object") {
        explanation =
          data.detailed_explaination ||
          data.detailed_explanation ||
          data.exaplation ||
          data.explanation ||
          data.explanations ||
          data.solution ||
          data.result ||
          data.data ||
          null;
        if (Array.isArray(explanation)) explanation = explanation.join("\n\n");
        if (explanation && typeof explanation === "object") {
          explanation = JSON.stringify(explanation, null, 2);
        }
      } else if (typeof data === "string") {
        explanation = data;
      }

      explanation = normalizeMathText(explanation || "No detailed explanation provided.");

      if (this.props.question_id) {
        this.saveToCache({
          storedAt: Date.now(),
          question_id: this.props.question_id,
          explanation,
        });
      }

      if (!this.isMounted_) return;
      this.setState({ explanation, loading: false });
    } catch (err) {
      if (!this.isMounted_) return;
      this.setState({ error: err.message || String(err), loading: false });
    }
  };

  // Explanation only - anything the model restated about the question is
  // dropped, since the selected question already has its own card.
  renderMathContent = (text) => {
    const html = renderExplanationHtml(stripQuestionEcho(text));
    return (
      <MathJax dynamic>
        <div
          className="dx-md"
          dangerouslySetInnerHTML={{
            __html: html || "<p style='color:#94a3b8'>No content available.</p>",
          }}
        />
      </MathJax>
    );
  };

  renderSelectedQuestion = () => {
    // Callers that already display the question themselves (the inline panel in
    // the answer view) opt out, so it is not shown twice.
    if (this.props.showQuestion === false) return null;

    const html = this.getQuestionHtml();
    if (!html) return null;

    return (
      <div className="dx-card dx-question">
        <div className="dx-label">
          <span className="dx-label-dot" />
          Question
        </div>
        <MathJax dynamic>
          <div
            className="dx-question-text dx-md"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </MathJax>      
      </div>
    );
  };

  render() {
    const { loading, error, explanation } = this.state;

    return (
      <div className="dx-panel">
        <style>
          {`
            @keyframes dxRise {
              from { opacity: 0; transform: translateY(10px); }
              to   { opacity: 1; transform: translateY(0); }
            }

            @keyframes dxShimmer {
              0%   { background-position: -420px 0; }
              100% { background-position: 420px 0; }
            }

            @keyframes dxOrbit { to { transform: rotate(360deg); } }

            .dx-panel { animation: dxRise 0.3s ease both; }

            .dx-card {
              background: #ffffff;
              border: 1px solid rgba(13, 148, 136, 0.16);
              border-radius: 18px;
              box-shadow: 0 8px 26px rgba(15, 23, 42, 0.07);
              animation: dxRise 0.35s ease both;
            }

            /* ============ Question recap ============ */
            .dx-question {
              position: relative;
              padding: 15px 16px 15px 20px;
              margin-bottom: 14px;
              overflow: hidden;
            }

            .dx-question::before {
              content: "";
              position: absolute;
              left: 0; top: 0; bottom: 0;
              width: 4px;
              background: linear-gradient(180deg, #0d9488, #0ea5e9);
            }

            .dx-label {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 11.5px;
              font-weight: 800;
              letter-spacing: 0.5px;
              text-transform: uppercase;
              color: #64748b;
              margin-bottom: 8px;
            }

            .dx-label-dot {
              width: 6px;
              height: 6px;
              border-radius: 50%;
              background: linear-gradient(135deg, #0d9488, #0ea5e9);
              flex: 0 0 6px;
            }

            .dx-question-text {
              font-size: 14px;
              font-weight: 600;
              line-height: 1.6;
              color: #0f172a;
            }

            /* The question keeps its own markup, so it needs the plain-HTML
               bits the markdown answer never produces. */
            .dx-question-plain { white-space: pre-wrap; }
            .dx-question-text sub, .dx-question-text sup {
              font-size: 75%;
              line-height: 0;
              position: relative;
            }
            .dx-question-text sup { top: -0.45em; }
            .dx-question-text sub { bottom: -0.2em; }
            .dx-question-text img {
              max-width: 100%;
              height: auto;
              vertical-align: middle;
            }
            .dx-question-text table { margin: 8px 0; }
            .dx-question-text .dx-math-block { text-align: left; }

            /* ============ Answer ============ */
            .dx-answer { padding: 18px; }

            .dx-answer-body {
              font-size: 13.5px;
              line-height: 1.7;
              color: #1e293b;
            }

            /* ============ Loading / states ============ */
            .dx-loader {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 14px;
              padding: 34px 18px;
            }

            .dx-spinner {
              width: 46px;
              height: 46px;
              border-radius: 50%;
              border: 3px solid rgba(13, 148, 136, 0.18);
              border-top-color: #0d9488;
              border-right-color: #0ea5e9;
              animation: dxOrbit 0.85s linear infinite;
            }

            .dx-loader-text { font-size: 13.5px; font-weight: 700; color: #0f766e; }
            .dx-loader-sub { font-size: 12px; color: #94a3b8; }

            .dx-skeleton {
              width: 100%;
              max-width: 460px;
              height: 12px;
              border-radius: 999px;
              background: linear-gradient(90deg, #eef1f8 25%, #e2e8f0 37%, #eef1f8 63%);
              background-size: 840px 100%;
              animation: dxShimmer 1.3s linear infinite;
            }

            .dx-state {
              display: flex;
              align-items: flex-start;
              gap: 12px;
              padding: 16px 18px;
              border-radius: 16px;
              font-size: 13px;
              font-weight: 600;
              animation: dxRise 0.3s ease both;
              overflow-wrap: anywhere;
            }

            .dx-state--error {
              color: #9f1239;
              background: linear-gradient(180deg, rgba(244,63,94,0.10), rgba(244,63,94,0.05));
              border: 1px solid rgba(244, 63, 94, 0.28);
            }

            .dx-state-icon { font-size: 18px; line-height: 1.2; }

            /* ============ Buttons ============ */
            .dx-actions {
              display: flex;
              justify-content: flex-end;
              flex-wrap: wrap;
              gap: 10px;
              margin-top: 14px;
            }

            .dx-btn {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 7px;
              padding: 9px 18px;
              border-radius: 999px;
              border: none;
              font-size: 13px;
              font-weight: 700;
              letter-spacing: 0.2px;
              color: #ffffff;
              cursor: pointer;
              transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
              white-space: nowrap;
            }

            .dx-btn:hover { transform: translateY(-2px); filter: brightness(1.05); }
            .dx-btn:active { transform: translateY(0); }
            .dx-btn:focus-visible { outline: 2px solid #0f766e; outline-offset: 2px; }

            .dx-btn--teal {
              background: linear-gradient(135deg, #0d9488, #0891b2);
              box-shadow: 0 8px 18px rgba(13, 148, 136, 0.30);
            }
            .dx-btn--teal:hover { box-shadow: 0 12px 24px rgba(13, 148, 136, 0.40); }

            /* ============ Markdown / MathJax ============ */
            /* No horizontal scrollbars: long text and formulas wrap instead. */
            .dx-md {
              overflow: visible;
              overflow-wrap: anywhere;
              word-break: break-word;
            }
            .dx-md > *:first-child { margin-top: 0 !important; }
            .dx-md > *:last-child { margin-bottom: 0 !important; }
            .dx-md img { max-width: 100%; height: auto; }
            .dx-md p, .dx-md li { margin: 6px 0; }
            .dx-md h1, .dx-md h2, .dx-md h3, .dx-md h4, .dx-md h5, .dx-md h6 {
              font-size: 15px;
              font-weight: 800;
              margin: 14px 0 6px;
              color: #0f172a;
            }
            .dx-md ul, .dx-md ol { padding-left: 20px; margin: 6px 0; }
            .dx-md hr { border: 0; border-top: 1px solid rgba(15, 23, 42, 0.10); margin: 14px 0; }
            .dx-md table { width: 100%; border-collapse: collapse; table-layout: fixed; margin: 8px 0; }
            .dx-md th, .dx-md td {
              border: 1px solid rgba(15, 23, 42, 0.10);
              padding: 6px 8px;
              overflow-wrap: anywhere;
            }
            .dx-md code {
              background: rgba(15, 23, 42, 0.06);
              padding: 1px 5px;
              border-radius: 5px;
            }
            .dx-md pre { white-space: pre-wrap; word-break: break-word; margin: 8px 0; }
            .dx-md blockquote {
              margin: 8px 0;
              padding: 6px 12px;
              border-left: 3px solid rgba(13, 148, 136, 0.4);
              background: rgba(13, 148, 136, 0.05);
              border-radius: 0 8px 8px 0;
            }

            /* Display formulas sit on their own line, centred and breathing. */
            .dx-math-block {
              display: block;
              margin: 12px 0;
              padding: 10px 12px;
              text-align: center;
              border-radius: 10px;
              background: rgba(13, 148, 136, 0.05);
              overflow-wrap: anywhere;
            }

            /* Inline formulas keep the text baseline and never break a word. */
            .dx-math-inline {
              display: inline;
              overflow-wrap: anywhere;
            }

            .dx-md mjx-container {
              max-width: 100% !important;
              overflow: visible !important;
              white-space: normal !important;
              display: inline-block;
            }
            .dx-md mjx-container mjx-math {
              max-width: 100% !important;
              white-space: normal !important;
              overflow-wrap: anywhere;
            }
            .dx-md mjx-container[display="true"] { display: block; }
            .dx-md .MathJax, .dx-md .mjx-chtml {
              max-width: 100% !important;
              white-space: normal !important;
              overflow: visible !important;
            }
            .dx-md svg { max-width: 100%; height: auto; }

            /* ============ Responsive ============ */
            @media (max-width: 767.98px) {
              .dx-answer { padding: 15px; }
              .dx-math-block { padding: 8px 10px; margin: 10px 0; }
              .dx-question-text { font-size: 13.5px; }
            }

            @media (max-width: 575.98px) {
              .dx-actions { flex-direction: column; align-items: stretch; }
              .dx-actions .dx-btn { width: 100%; }
              .dx-question { padding: 13px 13px 13px 16px; }
              .dx-question-text { font-size: 13px; }
              /* Wide questions (tables, long formulas) scroll on their own
                 instead of stretching the modal. */
              .dx-question-text table { display: block; overflow-x: auto; }
            }

            @media (prefers-reduced-motion: reduce) {
              .dx-panel, .dx-card, .dx-state, .dx-skeleton { animation: none !important; }
              .dx-btn { transition: none !important; }
            }
          `}
        </style>

        {/* The selected question is shown up front and stays visible while the
            explanation is loading, fails or arrives. */}
        {this.renderSelectedQuestion()}

        {loading && (
          <div className="dx-card dx-loader">
            <div className="dx-spinner" />
            <div className="dx-loader-text">Preparing the detailed explanation…</div>
            <div className="dx-loader-sub">This usually takes a few seconds</div>
            <div className="dx-skeleton" />
            <div className="dx-skeleton" style={{ maxWidth: 360 }} />
            <div className="dx-skeleton" style={{ maxWidth: 260 }} />
          </div>
        )}

        {!loading && error && (
          <>
            <div className="dx-state dx-state--error">
              <span className="dx-state-icon">⚠️</span>
              <span>{error}</span>
            </div>
            <div className="dx-actions">
              <button
                type="button"
                className="dx-btn dx-btn--teal"
                onClick={() => this.fetchDetailsExplanation()}
              >
                <span>🔄</span>Try Again
              </button>
            </div>
          </>
        )}

        {!loading && !error && (
          <div className="dx-card dx-answer">
            <div className="dx-label">
              <span className="dx-label-dot" />
              Explanation
            </div>
            <div className="dx-answer-body">{this.renderMathContent(explanation)}</div>
          </div>
        )}
      </div>
    );
  }
}

export default DetailsExplanation;
